import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
// eslint-disable-next-line no-restricted-imports
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { LoginService } from '@shared-angular/services/login.service';
import { RagLoggerService } from '@shared-angular/services/rag-logger/rag-logger.service';
import { ToastrService } from '@shared-angular/services/toastr.service';

import { Participant, RemoteParticipant, RemoteTrack, RemoteTrackPublication, Room, RoomEvent, Track } from 'livekit-client';
import { BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs';
import { BotPromptComponent } from 'src/app/shared/components/ai-bot/utils/bot-prompt/bot-prompt.component';
import { DisconnectOverlayComponent } from 'src/app/shared/components/disconnect-overlay/disconnect-overlay.component';
import { MicPermissionOverlayComponent } from 'src/app/shared/components/mic-permission-overlay/mic-permission-overlay.component';
import { AudioToTextService } from 'src/app/shared/services/audio-to-text/audio-to-text.service';
import { BotMessagesService } from 'src/app/shared/services/bot-messages.service';
import { BotService } from 'src/app/shared/services/bot.service';
import { EventManagerService } from 'src/app/shared/services/event-manager/event-manager.service';
import { IframeCommunicationService } from 'src/app/shared/services/iframe-communication/iframe-communication.service';
import { OverlayManagerService } from 'src/app/shared/services/overlay-manager.service';
import { PlayAudioService } from 'src/app/shared/services/playaudio.service';
import { VideoAvatarManagerService } from 'src/app/shared/services/ragai/video-avatar-manager.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { SpeechService } from 'src/app/shared/services/speech.service';
import { TurnLatencyService } from 'src/app/shared/services/turn-latency/turn-latency.service';
import { StatisticService } from '../admin/tabs/insights/statistics-tab/statistic.service';
import { AudioDebuggerComponent } from '../audionew/audio-debugger/audio-debugger.component';
import { AvatarFullscreenFooterButtonsComponent } from '../avatar/avatar-fullscreen-footer-buttons/avatar-fullscreen-footer-buttons.component';
import { AvatarFullscreenHeaderComponent } from '../avatar/avatar-fullscreen-header/avatar-fullscreen-header.component';
import { VoiceBarsComponent } from '../avatar/voice-bars/voice-bars.component';
import { BotBasedComponent } from '../bot-based/bot-based.component';

const HEYGEN_PARTICIPANT_IDENTITY = 'heygen';

@Component({
    styleUrl: './liveavatar.component.scss',
    templateUrl: './liveavatar.component.html',
    imports: [NgClass, NgStyle, NgTemplateOutlet, FormsModule, BotPromptComponent, DisconnectOverlayComponent, MicPermissionOverlayComponent, AudioDebuggerComponent, VoiceBarsComponent, AvatarFullscreenHeaderComponent, AvatarFullscreenFooterButtonsComponent],
    providers: [BsModalService]
})
export class LiveAvatarComponent extends BotBasedComponent implements OnInit, OnDestroy {
    @ViewChild('videoElement') private videoElement!: ElementRef<HTMLVideoElement>;
    @ViewChild('videoWrapper') private videoWrapper!: ElementRef<HTMLDivElement>;
    @ViewChild('backgroundImage') private backgroundImageEl!: ElementRef<HTMLDivElement>;
    @ViewChild('canvasElement') private canvasElement!: ElementRef<HTMLCanvasElement>;
    @ViewChild('fullscreenContent') private fullscreenContentEl?: ElementRef<HTMLDivElement>;

    public viewState: 'INITIAL' | 'CONNECTING' | 'CONNECTED' | 'DISCONNECTED' = 'INITIAL';
    public myWords: string = '';
    public botWords: string = '';
    public hasMsgBeenSend = false;
    public mediaWidth = 0;
    public mediaHeight = 0;
    public backgroundUrl: string = '';

    private room: Room | null = null;
    private wasSpeaking = false;
    private speechDoneFired = false;
    private speakEndDebounceTimer: number | null = null;
    private aspectRatio = 16 / 9;
    private readonly minVideoWidth = 400;
    private readonly minVideoHeight = 250;
    private isVirtualBgRunning = false;
    private virtualBgAnimationFrameId: number | null = null;
    private virtualBgRetryCount = 0;
    private readonly maxVirtualBgRetries = 40;
    private virtualBgRetryTimeoutId: number | null = null;
    private turnLatency = inject(TurnLatencyService);

    @HostListener('window:resize')
    public onResize(): void {
        this.resizeDiv();
    }

    constructor(
        public override route: ActivatedRoute,
        public override loginService: LoginService,
        public override speechService: SpeechService,
        public override statisticsService: StatisticService,
        public override iframeCommunication: IframeCommunicationService,
        public override playService: PlayAudioService,
        public override bot: BotService,
        public override socketService: SocketService,
        public override toastr: ToastrService,
        public override modalService: BsModalService,
        public override domSanatizer: DomSanitizer,
        protected override botMessages: BotMessagesService,
        public override logger: RagLoggerService,
        public attService: AudioToTextService,
        private eventManager: EventManagerService,
        private http: HttpClient,
        private videoAvatarManager: VideoAvatarManagerService,
        protected override overlayManager: OverlayManagerService
    ) {
        super(route, loginService, speechService, statisticsService, iframeCommunication, playService, bot, socketService, toastr, modalService, domSanatizer, botMessages, logger, overlayManager);
        // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        (window as any)["interrupt"] = this.stopAvatarSpeaking.bind(this);
        this.showMcpApprovalOverlayEnabled = true;
        this.backgroundUrl = this.botConfig.liveAvatarConfig?.backgroundUrl ?? '';
    }

    protected get muteButtonLabel(): string {
        return this.attService.isMuted
            ? $localize`:@@audio-unmute:Stummschaltung aufheben`
            : $localize`:@@audio-mute:Stummschalten`;
    }

    // Im Fullscreen-Modus ist Greenscreen immer aktiv, unabhaengig vom (dort disabled) useGreenscreen-Schalter.
    public get effectiveGreenscreen(): boolean {
        const config = this.botConfig.liveAvatarConfig;
        return !!config?.useGreenscreen || !!config?.fullscreenConfig?.active;
    }

    public get fullscreenActive(): boolean {
        return !!this.botConfig.liveAvatarConfig?.fullscreenConfig?.active;
    }

    public override async ngOnInit(): Promise<void> {
        await super.ngOnInit();
        this.iframeCommunication.RAGAI.WAITFOR._requireAvatarForInitialIdle();
        this.injectCSS(this.botConfig._id, 'avatar');
        this.resizeDiv();
        this.turnLatency.enable('liveavatar', this.botConfig._id);

        // Aspect ratio aus dem Vorschaubild ableiten, damit das Preview-Bild
        // schon vor dem Verbinden korrekt skaliert wird (Portrait vs. Landscape).
        const previewUrl = this.botConfig.liveAvatarConfig?.avatarPreviewUrl;
        if (previewUrl) {
            const img = new Image();
            img.onload = () => {
                if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                    this.aspectRatio = img.naturalWidth / img.naturalHeight;
                    this.resizeDiv();
                }
            };
            img.src = previewUrl;
        }

        // Barge-in-Dispatch: VideoAvatarManagerService.stop() kennt den Provider nicht selbst,
        // daher hier der providerspezifische Interrupt-Call (analog HeyGen-SDK avatar.interrupt()).
        this.eventManager.on('RAGAI.AVATAR.INTERRUPT', () => {
            void firstValueFrom(
                this.http.post(`/api/toolbox/liveavatar/interrupt/${this.botConfig._id}`, {})
            ).catch(() => { });
        });

        this.subscriptions.push(
            // Mikrofon-Eingabe → Nachricht senden
            this.attService.textReceived.subscribe(async (text) => {
                this.myWords = text;
                if (this.attService.lastVoiceTiming) this.turnLatency.startVoiceTurn(this.attService.lastVoiceTiming);
                await this.add(text);
            }),

            // Hintergrundbild wurde per Blockly-Block (AVATAR_setBGImg/unsetBGImg) geändert
            this.videoAvatarManager.onBackgroundUpdate.subscribe((keyValEntry) => {
                this.backgroundUrl = keyValEntry.value as string;
            }),
            this.videoAvatarManager.onBackgroundDelete.subscribe(() => {
                this.backgroundUrl = this.botConfig.liveAvatarConfig?.backgroundUrl ?? '';
            }),

            // Aufnahme gestartet → Bot-Denken abbrechen + laufende Sprachausgabe unterbrechen (Barge-in)
            this.attService.recStarted.subscribe(async (started) => {
                if (started) {
                    await this.stopAvatarSpeaking();
                }
            }),

            // Bot antwortet (Streaming) → Mic-Threshold erhöhen
            this.socketService.onRunUpdate.subscribe(() => {
                this.attService.speakingState.next(true);
                this.attService.speechRecognitionThresholdElevationMultiplyer = 2;
            }),

            // Bot-Antwort fertig → Mic-Threshold zurücksetzen (MCP-Rollen ignorieren, keine Sprachausgabe dafuer)
            this.socketService.onRunComplete.subscribe((event) => {
                if (event.role !== 'assistant') return;
                this.botWords = event.content
                    .filter(c => c.type === 'text')
                    .map(c => (c).text)
                    .join(' ');
                this.attService.speakingState.next(false);
                this.attService.speechRecognitionThresholdElevationMultiplyer = 1;
            }),

            // State-Update → thinking-Flag + Avatar-speaking + Auto-Mic bei Idle
            this.socketService.onStateUpdate.subscribe((data) => {
                this.isThinking = data.sessionState === 'thinking';
                if (data.sessionState === 'thinking') {
                    this.eventManager.triggerEventHook('RAGAI.AVATAR.SPEECH.thinking', {});
                }
                if (data.sessionState === 'speaking' || data.sessionState === 'generating_audio') {
                    if (!this.wasSpeaking) {
                        this.eventManager.triggerEventHook('RAGAI.AVATAR.SPEECH.play', {});
                    }
                    this.wasSpeaking = true;
                    this.attService.speakingState.next(true);
                    this.attService.speechRecognitionThresholdElevationMultiplyer = 2;
                }
                if (data.sessionState === 'idle') {
                    this.attService.speakingState.next(false);
                    this.attService.speechRecognitionThresholdElevationMultiplyer = 1;
                    if (this.wasSpeaking && !this.speechDoneFired) {
                        // Fallback: ActiveSpeakersChanged hat nicht gefeuert
                        this.speechDoneFired = true;
                        this.eventManager.triggerEventHook('RAGAI.AVATAR.SPEECH.done', {});
                    }
                    this.wasSpeaking = false;
                    this.speechDoneFired = false;
                    // Mikrofon ist immer verbunden (siehe connectLiveAvatar) - nur neu starten, Mute-Status bleibt unveraendert
                    this.attService.startRecording();
                }
            })
        );
    }

    public async connectLiveAvatar(): Promise<void> {
        this.viewState = 'CONNECTING';
        try {
            const { livekitUrl, livekitClientToken } = await firstValueFrom(
                this.http.post<{ livekitUrl: string; livekitClientToken: string }>(
                    `/api/toolbox/liveavatar/session/${this.botConfig._id}`, {}
                )
            );

            this.room = new Room();

            this.room.on(
                RoomEvent.TrackSubscribed,
                (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
                    if (participant.identity !== HEYGEN_PARTICIPANT_IDENTITY) return;
                    if (track.kind === Track.Kind.Video || track.kind === Track.Kind.Audio) {
                        track.attach(this.videoElement.nativeElement);
                    }
                    if (track.kind === Track.Kind.Audio) {
                        this.turnLatency.attachAudio(new MediaStream([track.mediaStreamTrack]));
                    }
                    if (track.kind === Track.Kind.Video) {
                        this.viewState = 'CONNECTED';
                        this.iframeCommunication.RAGAI.WAITFOR._avatarConnected();
                        // Mikrofon verbindet sich immer; voiceInputActive entscheidet nur, ob es sofort
                        // unmuted zuhoert oder zunaechst stummgeschaltet bleibt (manuell per Mute-Button aktivierbar).
                        this.attService.isMuted = !this.botConfig.liveAvatarConfig?.voiceInputActive;
                        this.attService.state.next('connecting');
                        this.attService.startRecording();
                        setTimeout(() => {
                            this.eventManager.triggerEventHook('io_out_registerAvatarSession', [{ sessionId: this.botConfig._id }]);
                            this.resizeDiv();
                            this.tryStartVirtualBackground();
                            // Direkt nach Verbindungsaufbau gilt der Avatar als "fertig gesprochen" (zuhoerend) -
                            // ohne diesen Trigger bleibt der per Blockly gesetzte Statustext bis zur ersten
                            // Sprachausgabe leer und es wird der eingebaute Standardtext angezeigt. Verzoegert
                            // per Makrotask, da RAGAI.WAITFOR._avatarConnected() (oben) das Registrieren der
                            // Blockly-Listener selbst erst ueber einen await-Ketten-Umweg anstoesst - ohne
                            // Makrotask-Grenze koennte dieser Trigger vor der Listener-Registrierung feuern.
                            this.eventManager.triggerEventHook('RAGAI.AVATAR.SPEECH.done', {});
                            // Feuert bei JEDEM Connect (auch Reconnect) - im Gegensatz zu RAGAI.WAITFOR.initialIdle(),
                            // das nur beim allerersten Verbindungsaufbau der Seite aufloest (siehe WaitForManagerService).
                            this.eventManager.triggerEventHook('RAGAI.AVATAR.CONNECTED', {});
                        }, 100);
                    }
                }
            );

            this.room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
                const heygenSpeaking = speakers.some(s => s.identity === HEYGEN_PARTICIPANT_IDENTITY);
                if (heygenSpeaking) {
                    // Avatar spricht wieder → laufenden Debounce-Timer verwerfen
                    if (this.speakEndDebounceTimer !== null) {
                        clearTimeout(this.speakEndDebounceTimer);
                        this.speakEndDebounceTimer = null;
                    }
                    this.speechDoneFired = false;
                } else if (this.wasSpeaking && !this.speechDoneFired) {
                    // Avatar hat aufgehört zu sprechen → 400 ms Debounce gegen kurze Pausen
                    if (this.speakEndDebounceTimer !== null) clearTimeout(this.speakEndDebounceTimer);
                    this.speakEndDebounceTimer = window.setTimeout(() => {
                        this.speakEndDebounceTimer = null;
                        if (this.wasSpeaking && !this.speechDoneFired) {
                            this.speechDoneFired = true;
                            this.eventManager.triggerEventHook('RAGAI.AVATAR.SPEECH.done', {});
                        }
                    }, 400);
                }
            });

            this.room.on(RoomEvent.Disconnected, () => {
                this.stopVirtualBackground();
                this.videoAvatarManager.clearFooterButtons();
                this.viewState = 'INITIAL';
                this.room = null;
            });

            await this.room.connect(livekitUrl, livekitClientToken);
        } catch (err) {
            console.error('[LiveAvatarComponent] connect error:', err);
            if (err instanceof HttpErrorResponse && err.status === 400 && (err.error as { error?: string }).error) {
                this.toastr.error('ERROR', (err.error as { error?: string }).error ?? '');
            } else {
                this.toastr.error('ERROR_500', $localize`:@@liveavatar-connect-error:Die Verbindung zum Avatar ist fehlgeschlagen. Versuche es erneut.`);
            }
            this.videoAvatarManager.clearFooterButtons();
            this.viewState = 'INITIAL';
        }
    }

    // Barge-in: bricht Bot-Denken und eine laufende Sprachausgabe des Avatars ab (analog zum
    // HeyGen-SDK avatar.interrupt() im alten avatar.component.ts). Auch über RAGAI.VIDEO_AVATAR.stop() ausloesbar.
    public async stopAvatarSpeaking(): Promise<void> {
        await this.videoAvatarManager.stop();
        this.hasMsgBeenSend = false;
    }

    // add() erhält den Text als nonPromtInput-Argument und leert daher nur this.promptInput
    // (BotBasedComponent), nicht das an das Eingabefeld gebundene this.myWords - deshalb hier explizit leeren.
    public async sendMyWords(): Promise<void> {
        const text = this.myWords;
        this.myWords = '';
        this.turnLatency.startTextTurn();
        await this.add(text);
    }

    public async disconnectLiveAvatar(): Promise<void> {
        if (this.speakEndDebounceTimer !== null) {
            clearTimeout(this.speakEndDebounceTimer);
            this.speakEndDebounceTimer = null;
        }
        this.stopVirtualBackground();
        this.videoAvatarManager.clearFooterButtons();
        this.attService.setMuted(true);
        if (this.room) {
            await this.room.disconnect();
            this.room = null;
        }
        await firstValueFrom(
            this.http.delete(`/api/toolbox/liveavatar/session/${this.botConfig._id}`)
        ).catch(() => { });
        this.viewState = 'INITIAL';
    }

    public getOverWriteText(): string {
        return window.avatarbot_overrideText ?? '';
    }

    // Unterscheidet "nie gesetzt" (Standard-Status anzeigen) von "explizit leer gesetzt" (nichts anzeigen)
    public hasOverrideText(): boolean {
        return window.avatarbot_overrideText !== undefined;
    }

    /** Wird vom <video>-Element bei loadedmetadata aufgerufen */
    public onVideoLoadedMetadata(): void {
        const video = this.videoElement.nativeElement;
        if (video.videoWidth > 0 && video.videoHeight > 0) {
            this.aspectRatio = video.videoWidth / video.videoHeight;
            this.resizeDiv();
        }
        this.tryStartVirtualBackground();
    }

    /** Wird vom <video>-Element bei playing aufgerufen */
    public onVideoPlaying(): void {
        this.tryStartVirtualBackground();
    }

    // ─── Responsive Sizing ───────────────────────────────────────────────────

    private resizeDiv(): void {
        // Im Fullscreen-Modus wird relativ zur Content-Flaeche (nicht zum Fenster) skaliert, mit kleinerem
        // Fuellfaktor je nach Ausrichtung, damit der Avatar sichtbar nicht die komplette Flaeche ausfuellt.
        const contentEl = this.fullscreenActive ? this.fullscreenContentEl?.nativeElement : undefined;
        const basisWidth = contentEl?.clientWidth || window.innerWidth;
        const basisHeight = contentEl?.clientHeight || window.innerHeight;
        const fillRatio = contentEl ? (window.matchMedia('(orientation: portrait)').matches ? 0.95 : 0.9) : 0.8;

        const maxWidth = Math.max(basisWidth * fillRatio, this.minVideoWidth);
        const maxHeight = Math.max(basisHeight * fillRatio, this.minVideoHeight);

        let width = maxWidth;
        let height = Math.max(width / this.aspectRatio, this.minVideoHeight);

        if (height > maxHeight) {
            height = Math.max(maxHeight, this.minVideoHeight);
            width = Math.max(height * this.aspectRatio, this.minVideoWidth);
        }

        const px = (n: number) => `${String(n)}px`;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (this.videoElement.nativeElement) {
            this.videoElement.nativeElement.style.width = px(width);
            this.videoElement.nativeElement.style.height = px(height);
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (this.backgroundImageEl.nativeElement) {
            this.backgroundImageEl.nativeElement.style.width = px(width);
            this.backgroundImageEl.nativeElement.style.height = px(height);
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (this.canvasElement.nativeElement) {
            this.canvasElement.nativeElement.style.width = px(width);
            this.canvasElement.nativeElement.style.height = px(height);
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (this.videoWrapper.nativeElement) {
            this.videoWrapper.nativeElement.style.width = px(width);
            this.videoWrapper.nativeElement.style.height = px(height);
        }

        this.mediaWidth = width;
        this.mediaHeight = height;
    }

    // ─── Virtual Background / Greenscreen ────────────────────────────────────

    private tryStartVirtualBackground(): void {
        if (!this.effectiveGreenscreen) return;
        if (this.viewState !== 'CONNECTED' || this.isVirtualBgRunning) return;

        const canvas = this.canvasElement.nativeElement;
        const video = this.videoElement.nativeElement;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!canvas) {
            this.scheduleVirtualBgRetry();
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
            this.scheduleVirtualBgRetry();
            return;
        }

        if (this.virtualBgRetryTimeoutId !== null) {
            clearTimeout(this.virtualBgRetryTimeoutId);
            this.virtualBgRetryTimeoutId = null;
        }
        this.virtualBgRetryCount = 0;
        this.isVirtualBgRunning = true;

        // Aspect ratio hier aktualisieren, da onVideoLoadedMetadata bei Live-Streams
        // ggf. noch videoWidth === 0 hatte und den Wert nicht setzen konnte.
        const actualRatio = video.videoWidth / video.videoHeight;
        if (actualRatio !== this.aspectRatio) {
            this.aspectRatio = actualRatio;
            this.resizeDiv();
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        this.calcVirtualBg();
    }

    private scheduleVirtualBgRetry(): void {
        if (this.isVirtualBgRunning || this.virtualBgRetryCount >= this.maxVirtualBgRetries) return;
        if (this.virtualBgRetryTimeoutId !== null) return;

        this.virtualBgRetryCount++;
        this.virtualBgRetryTimeoutId = window.setTimeout(() => {
            this.virtualBgRetryTimeoutId = null;
            this.tryStartVirtualBackground();
        }, 200);
    }

    private stopVirtualBackground(): void {
        this.isVirtualBgRunning = false;
        this.virtualBgRetryCount = 0;
        if (this.virtualBgAnimationFrameId !== null) {
            cancelAnimationFrame(this.virtualBgAnimationFrameId);
            this.virtualBgAnimationFrameId = null;
        }
        if (this.virtualBgRetryTimeoutId !== null) {
            clearTimeout(this.virtualBgRetryTimeoutId);
            this.virtualBgRetryTimeoutId = null;
        }
    }

    public calcVirtualBg(): void {
        if (!this.isVirtualBgRunning) return;
        try {
            const video = this.videoElement.nativeElement;
            const canvas = this.canvasElement.nativeElement;
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!canvas || !video) {
                this.virtualBgAnimationFrameId = requestAnimationFrame(this.calcVirtualBg.bind(this));
                return;
            }
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) {
                this.virtualBgAnimationFrameId = requestAnimationFrame(this.calcVirtualBg.bind(this));
                return;
            }
            if (video.videoWidth === 0 || video.videoHeight === 0) {
                this.virtualBgAnimationFrameId = requestAnimationFrame(this.calcVirtualBg.bind(this));
                return;
            }

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            for (let i = 0; i < data.length; i += 4) {
                const r = data[i] ?? 0;
                const g = data[i + 1] ?? 0;
                const b = data[i + 2] ?? 0;
                if (g > 100 && r < 100 && b < 100) {
                    data[i + 3] = 0;
                }
            }
            ctx.putImageData(imageData, 0, 0);
        } catch (_e) {
            console.warn('[LiveAvatarComponent] Virtual background error', _e);
        }
        this.virtualBgAnimationFrameId = requestAnimationFrame(this.calcVirtualBg.bind(this));
    }

    public override async ngOnDestroy(): Promise<void> {
        this.turnLatency.disable();
        await this.disconnectLiveAvatar();
        super.ngOnDestroy();
    }
}
